import { Controller } from "@hotwired/stimulus"

// The in-canvas comment thread's actions: the footer bar (Resolve / Reply / Dismiss,
// with Resolve↔Reopen swapping by status) and the card-level kebab (Delete) for the
// thread's top-level comment.
//
// The thread card is appended to <body> (see course-review-canvas), so — exactly
// like the composer — it carries its own controller; a data-action here could not
// resolve against the canvas controller further up the tree. Every action hits the
// same comments endpoint the Review Activity panel uses and announces the change
// through `course-review:changed`, which repaints the markers and closes this card.
export default class extends Controller {
  static targets = ["menu"]
  static values = { commentsUrl: String, context: Object }

  connect() {
    this.onDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener("click", this.onDocumentClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick)
  }

  toggleMenu(event) {
    event.stopPropagation()
    if (!this.hasMenuTarget) return

    const opening = this.menuTarget.hidden
    this.menuTarget.hidden = !opening
    event.currentTarget.setAttribute("aria-expanded", opening ? "true" : "false")
  }

  // A click anywhere outside the toolbar closes the menu — it is a popover, not a
  // pinned panel.
  handleDocumentClick(event) {
    if (event.target.closest(".course-review-thread__toolbar")) return

    this.closeMenu()
  }

  closeMenu() {
    if (this.hasMenuTarget) this.menuTarget.hidden = true
    this.element.querySelector(".course-review-thread__kebab")?.setAttribute("aria-expanded", "false")
  }

  // Swap the action bar for the reply composer: hide the Resolve/Reply/Dismiss footer
  // and reveal + focus the composer the canvas hosted (collapsed) inside this card.
  focusReply() {
    const composer = this.element.querySelector(".course-review-composer")
    if (!composer) return

    // A stale Resolve/Dismiss error would otherwise sit above the composer it has
    // nothing to do with.
    this.showError(null)
    this.toggleFooter(false)
    composer.classList.remove("course-review-composer--collapsed")
    composer.querySelector("[data-course-review-composer-target='body']")?.focus()
  }

  // Cancel/Escape from the hosted reply composer (which has already cleared its draft):
  // tuck it away and bring the Resolve/Reply/Dismiss bar back.
  cancelReply() {
    this.showError(null)
    this.toggleFooter(true)
    this.element.querySelector(".course-review-composer")?.classList.add("course-review-composer--collapsed")
  }

  toggleFooter(visible) {
    const footer = this.element.querySelector("[data-thread-footer]")
    if (footer) footer.hidden = !visible
  }

  resolveComment() {
    this.setStatus("resolved")
  }

  dismissComment() {
    this.setStatus("dismissed")
  }

  reopenComment() {
    this.setStatus("pending")
  }

  setStatus(status) {
    this.changeComment({ method: "PATCH", comment: { status } })
  }

  deleteComment() {
    this.changeComment({ method: "DELETE" })
  }

  // Mirrors course-review-activity#changeComment: the row, the panel and the marker
  // are all server-rendered, so a successful change asks for a re-render rather than
  // editing any of them here.
  async changeComment({ method, comment = null }) {
    const commentId = this.element.dataset.commentId
    if (!commentId || !this.hasCommentsUrlValue) return

    this.closeMenu()
    this.showError(null)

    try {
      const response = await fetch(`${this.commentsUrlValue}/${commentId}`, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ ...this.contextValue, ...(comment ? { comment } : {}) })
      })

      const payload = await response.json()
      if (!response.ok || !payload.success) {
        throw Object.assign(new Error("comment change failed"), { serverErrors: payload.errors })
      }

      window.dispatchEvent(new CustomEvent("course-review:changed"))
    } catch (error) {
      // The markers stay as they are and the reviewer can retry, but the card says
      // why the change was refused instead of silently ignoring the click.
      const message = Array.isArray(error.serverErrors) ? error.serverErrors.filter(Boolean).join(" ") : ""
      this.showError(message || "Couldn't save your change. Try again.")
    }
  }

  // Pass null to clear. Only the text changes: the role="alert" region stays rendered so
  // screen readers announce the message (the CSS collapses it while empty).
  showError(message) {
    const error = this.element.querySelector("[data-thread-error]")
    if (error) error.textContent = message || ""
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
